/* eslint-disable jsx-a11y/media-has-caption */
import { LOADING, SUCCESS, ERROR, useStatus } from '@rootstrap/redux-tools';
import Button from 'components/common/Button/Button';
import Input from 'components/common/Input/Input';
import Loading from 'components/common/Loading';
import { LogViewer, useLogger } from 'components/common/LogViewer';
import { contentOptions, imageSizes, videoUploadsType } from 'constants/constants';
import { useForm, useNavigation, useTextInputProps, useToast, useValidation } from 'hooks';
import { podcastValidation, ValidationMessages } from 'utils/constraints';
import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import {
  checkImageDimensions,
  handleDateKeyDown,
  routeWithProps,
  validateVideo
} from 'utils/helpers';
import Select from 'react-select';
import cn from 'classnames';
import ImageCropperModal from 'components/common/ImageCropper/ImageCropperModal';
import { customDropDownStyles } from 'styles/customDropDownStyles';
import { getTodayDate, timeExtract } from 'utils/date';
import { some } from 'lodash';
import { routesPaths } from 'constants/routesPaths';
import parseError from 'utils/parseError';
import CustomCategory from 'components/CustomCategory/CustomCategory';
import Switch from 'react-switch';
import { ValidationErrors } from 'utils/validationErrors';
import { useDispatch } from 'react-redux';
import { createPodcast, processPodcast } from 'state/actions/podcastActions';
import UserService from 'services/userService';
import PodcastService from 'services/podcastService';
import axios from 'axios';
import useUploadingPodcast from 'hooks/podcast/useUploadingPodcast';
import { Line } from 'rc-progress';
import categoriesService from 'services/categoriesService';
import useUpdatePodcast from 'hooks/podcast/useUpdatePodcast';
import { convertTiffToJpeg } from 'components/common/ImageCropper/UrlToImage';
import { convertHeicToJpeg } from 'utils/heicConverter';
import useFileUpload from 'hooks/common/useFileUpload';

const fields = {
  title: 'title',
  description: 'description',
  start_date: 'start_date',
  duration: 'duration',
  timezone: 'timezone',
  start_time: 'start_time',
  members_price: 'members_price',
  non_members_price: 'non_members_price',
  banner_image_url: 'banner_image_url',
  notify_subscribers: 'notify_subscribers',
  category_ids: 'category_ids',
  lead_cast: 'lead_cast',
  credits: 'credits',
  contentViewerCategory: 'contentViewerCategory',
  podcastVideo: 'podcastVideo'
};

const AddPodcastForm = ({ podcastId, celId }) => {
  const dispatch = useDispatch();
  const { goToWithSearch } = useNavigation();
  const podcastRef = useRef(null);
  const username = useRef();
  const desktopCoverRef = useRef(null);
  const [desktopCoverPic, setDesktopCoverPic] = useState(null);
  const [podcast, setPodcast] = useState(null);
  const celebrityId = useRef();
  const { error, status } = useStatus(createPodcast);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [croppingUrlMimeType, setCroppingUrlMimeType] = useState();
  const [croppingUrl, setCroppingUrl] = useState();
  const [aspectRatio, setAspectRatio] = useState();
  const checkType = useRef();
  const { showErrorToast, showToast } = useToast();
  const validator = useValidation(podcastValidation);
  const [subscribedFans, setSubScribedFans] = useState(true);
  const [duration, setDuration] = useState('');
  const [time, setTime] = useState('');
  const [membersPrice, setMembersPrice] = useState('');
  const [nonMembersPrice, setNonMembersPrice] = useState('');
  const [podcastData, setPodcastData] = useState();
  const [selectedContent, setSelectedContent] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isDesktopBannerFalse, setIsDesktopBannerFalse] = useState(false);
  const [audio, setAudio] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [categories, setCategories] = useState();
  const [processLoading, setProcessLoading] = useState(false);
  const { loading, uploadingPodcast } = useUploadingPodcast();
  const { updatePodcast, isLoading } = useUpdatePodcast();
  const [uploadStage, setUploadStage] = useState(''); // 'validating', 'validated', 'uploading', 'processing', 'completed'
  const [formErrors, setFormErrors] = useState({});

  const { logs, showLogs, setShowLogs, logInfo, logWarning, logError, logApiError } = useLogger();

    // Detect Safari browser
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Initialize file upload hook
  const {
    uploadFile,
    uploadProgress: fileUploadProgress,
    uploadStage: fileUploadStage,
    currentFileName,
    setUploadStage: setFileUploadStage,
    setUploadProgress: setFileUploadProgress,
    setCurrentFileName
  } = useFileUpload({
    onUploadProgress: progress => {
      setFileUploadProgress(progress);
    },
    onUploadComplete: () => {
      setFileUploadStage('processing');
    },
    onUploadError: error => {
      logError('Error uploading file', error);
      setFileUploadStage('');
    },
    getMultipartUploadUrl: (uploadId, mediaId, partNumber) =>
      PodcastService.getMultipartUploadUrl(uploadId, mediaId, partNumber),
    completeMultipartUpload: (uploadId, mediaId, parts) =>
      PodcastService.completeMultipartUpload(uploadId, mediaId, parts),
    onRedirect: id => navigateToEditPodcast(id),
    redirectId: podcastId
  });

  const handleFormSubmit = async data => {
    logInfo('Starting form submission process');
    setUploadProgress(0);
    if (podcastId) {
      logInfo('Updating existing podcast', { podcastId });
      const categoryIds = selectedCategories.map(({ value }) => value);
      const updateData = {
        title: data.title,
        description: data.description,
        lead_cast: data.lead_cast || '',
        credits: data.credits || '',
        content_viewer_category: selectedContent.value,
        start_time: data.start_time,
        start_date: data.start_date,
        members_price: data.members_price ?? 0,
        non_members_price: data.non_members_price ?? 0,
        audio_only: audio,
        category_ids: categoryIds,
        duration
      };

      logInfo('Preparing form data for podcast update', updateData);
      const formData = new FormData();
      formData.append('podcast[banner]', desktopCoverPic);
      Object.keys(updateData).forEach(key => {
        if (key === 'category_ids') {
          updateData[key].forEach(id => {
            formData.append(`podcast[${key}][]`, id);
          });
        } else {
          formData.append(`podcast[${key}]`, updateData[key]);
        }
      });

      logInfo('Calling updatePodcast API', { celebrityId: celebrityId.current, podcastId });
      updatePodcast(celebrityId.current, podcastId, formData);
    } else {
      logInfo('Creating new podcast');
      const categoryIds = selectedCategories.map(({ value }) => value);
      data.audio_only = audio;

      logInfo('Preparing form data for podcast creation', {
        title: data.title,
        categoryIds,
        audio_only: data.audio_only
      });

      const formData = new FormData();
      formData.append('podcast[title]', data.title);
      formData.append('podcast[description]', data.description);
      categoryIds.forEach(id => {
        formData.append('podcast[category_ids][]', id);
      });
      formData.append('podcast[lead_cast]', data.lead_cast || '');
      formData.append('podcast[content_viewer_category]', data.contentViewerCategory);
      formData.append('podcast[credits]', data.credits || '');
      formData.append('podcast[start_time]', data.start_time);
      formData.append('podcast[start_date]', data.start_date);
      formData.append('podcast[duration]', duration);
      formData.append('podcast[members_price]', data.members_price ?? 0);
      formData.append('podcast[non_members_price]', data.non_members_price ?? 0);
      formData.append('podcast[notify_subscribers]', data.notify_subscribers);
      formData.append('podcast[audio_only]', audio);
      formData.append('podcast[banner]]', data.banner_image_url);

      // Add multipart upload flags if needed
      if (podcast && podcast.size > 100 * 1024 * 1024) { // 100MB threshold
        formData.append('podcast[large_file]', true);
        formData.append('podcast[mime_type]', podcast.type);
      }

      logInfo('Dispatching createPodcast action');
      dispatch(createPodcast(celebrityId, formData));
    }
  };
  const { values, errors, handleValueChange, handleSubmit, handleBlur, setValues, setErrors } = useForm(
    {
      onSubmit: handleFormSubmit,
      validateOnBlur: true,
      validateOnChange: true,
      initialValues: {
        [fields.notify_subscribers]: true,
        [fields.audio_only]: false,
        // Set a default date for Safari to fix form validation
        ...(isSafari && { [fields.start_date]: getTodayDate() })
      },
      validator
    },
    [handleFormSubmit]
  );

  const inputProps = useTextInputProps(handleValueChange, handleBlur, values);

  const onCompleteVideoUpload = () => {
    logInfo('Podcast upload completed, starting processing', {
      celebrityId: celebrityId.current,
      podcastId: uploadingPodcast.id
    });
    setProcessLoading(true);
    setUploadStage('processing');
    dispatch(processPodcast(celebrityId.current, uploadingPodcast.id, podcast));
  };

  const { status: processPodcastStatus, error: processPodcastError } = useStatus(processPodcast);

  useEffect(() => {
    const processPodcastAsync = async () => {
      if (processPodcastStatus === SUCCESS) {
        logInfo('Podcast processed successfully', { status: processPodcastStatus });
        await dispatch(processPodcast.reset());
        setProcessLoading(false);
        setUploadStage('completed');
        
        // Show success message
        showToast('Podcast uploaded successfully!');
        
        // Add delay before redirect
        setTimeout(() => {
          const route = routeWithProps(routesPaths.celebrityId, {
            username: username.current.toLowerCase()
          });
          goToWithSearch(route, `?tab=4`);
        }, 2000);
      } else if (processPodcastStatus === ERROR) {
        logError('Error processing podcast', { error: processPodcastError }, 'error');
        showErrorToast('Error processing podcast. Please try again.');
        setProcessLoading(false);
        setUploadStage('');
      }
    };
    processPodcastAsync();
  }, [
    dispatch,
    celebrityId,
    processPodcastStatus,
    processPodcastError,
    goToWithSearch,
    logInfo,
    logError,
    showToast,
    showErrorToast
  ]);

  useEffect(() => {
    const uploadPodcastFiles = async() =>{
      if (status === SUCCESS && (uploadingPodcast?.uploadUrl || uploadingPodcast?.uploadData?.uploadId)) {
        logInfo('Received upload URL from API', { 
          uploadUrl: uploadingPodcast.uploadUrl,
          uploadId: uploadingPodcast.uploadData?.uploadId,
          mediaId: uploadingPodcast.uploadData?.mediaId
        });
        dispatch(createPodcast.reset());
        setProcessLoading(true);
        setFileUploadStage('validated');
        setUploadProgress(0);
        // Use the common upload hook
        if (uploadingPodcast.uploadData?.uploadId) {
          // Multipart upload
         await uploadFile(
            null, 
            podcast, 
            uploadingPodcast.uploadData.uploadId, 
            uploadingPodcast.uploadData.mediaId
          );
        } else if (uploadingPodcast.uploadUrl) {
          // Standard upload
         await uploadFile(uploadingPodcast.uploadUrl, podcast);
        } else {
          logError('No valid upload URL or uploadId received', {
            uploadUrl: uploadingPodcast.uploadUrl,
            uploadData: uploadingPodcast.uploadData
          });
          showErrorToast('Error: No valid upload URL received');
          setProcessLoading(false);
          setFileUploadStage('');
        }
        onCompleteVideoUpload();
      } else if (status === ERROR) {
        logError('Error creating podcast', { error });
        showErrorToast('Error creating podcast. Please try again.');
        setProcessLoading(false);
        setFileUploadStage('');
      }
    }
    uploadPodcastFiles();
  }, [dispatch, status, error, uploadingPodcast?.uploadUrl, uploadingPodcast?.uploadData]);

  const handleContentChange = e => {
    setSelectedContent(e);
    handleValueChange(fields.contentViewerCategory, e.value);
  };

  const handleCategoryChange = selectedOptions => {
    setSelectedCategories(selectedOptions);
    handleValueChange(fields.category_ids, selectedOptions);
  };

  const handleDesktopCoverUpload = () => {
    desktopCoverRef.current.click();
  };

  const handleDesktopCoverChange = async e => {
    const file = e.target.files[0];

    try {
      let processedFile = file;
      let imageUrl;

      // Handle HEIC files first
      const isHeicType =
        /^image\/(heic|heif|heic-sequence|heif-sequence)$/i.test(file.type) ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif');

      if (isHeicType) {
        processedFile = await convertHeicToJpeg(file);
        imageUrl = URL.createObjectURL(processedFile);
      }
      // Then handle TIFF files
      else if (file.type === 'image/tiff') {
        const jpegDataUrl = await convertTiffToJpeg(file);
        if (!jpegDataUrl) {
          showErrorToast('Failed to convert TIFF image');
          return;
        }
        imageUrl = jpegDataUrl;

        const response = await fetch(jpegDataUrl);
        const blob = await response.blob();
        processedFile = new File([blob], file.name.replace('.tiff', '.jpg'), {
          type: 'image/jpeg'
        });
      } else {
        imageUrl = URL.createObjectURL(file);
      }

      const result = await checkImageDimensions(
        processedFile,
        imageSizes.eventDesktopCover.width,
        imageSizes.eventDesktopCover.height
      );

      if (result.isValid) {
        setCropModalOpen(true);
        setAspectRatio(16 / 9);
        setCroppingUrlMimeType(processedFile.type);
        setCroppingUrl(imageUrl);
        checkType.current = 'desktop';
      } else {
        showErrorToast(result.error);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      showErrorToast('Failed to process image');
    }
  };

  const setFormError = useCallback((field, error) => {
    setFormErrors(prev => ({
      ...prev,
      [field]: error ? [error] : undefined
    }));
  }, []);

  const onChangeMembersPrice = event => {
    let value = event.target.value.replace(/[^0-9.]/g, '');
    if (value.length > 0) {
      value = `$${value}`;
      const valueWithoutDollar = value.slice(1);
      handleValueChange(fields.members_price, valueWithoutDollar);
      setMembersPrice(value);

      // Validate member price min/max limits
      const memberValue = parseFloat(valueWithoutDollar);
      if (!isNaN(memberValue)) {
        if (memberValue > 0 && memberValue < 0.5) {
          setFormError(fields.members_price, ValidationMessages.MIN_PRICE);
        } else if (memberValue >= 1000) {
          setFormError(fields.members_price, ValidationMessages.MAX_PRICE);
        } else {
          setFormError(fields.members_price, undefined);
        }
      }

      // Check if non-member price has min/max validation errors before overriding
      const nonMemberValue = nonMembersPrice ? parseFloat(nonMembersPrice.replace('$', '')) : 0;
      let preserveNonMemberError = false;
      
      if (!isNaN(nonMemberValue)) {
        if ((nonMemberValue > 0 && nonMemberValue < 0.5) || nonMemberValue >= 1000) {
          // Preserve existing min/max validation errors
          preserveNonMemberError = true;
        }
      }
      
      // Only update non-member price relationship error if there's no min/max error
      if (!preserveNonMemberError) {
        // Validate non-member price relationship - use only formErrors for price validation
        if (!nonMembersPrice || nonMembersPrice === '$' || nonMembersPrice === '$0') {
          setFormError(fields.non_members_price, ValidationMessages.NON_MEMBER_PRICE_LOWER);
        } else {
          if (!isNaN(nonMemberValue) && !isNaN(memberValue) && nonMemberValue < memberValue) {
            setFormError(fields.non_members_price, ValidationMessages.NON_MEMBER_PRICE_LOWER);
          } else {
            setFormError(fields.non_members_price, undefined);
          }
        }
      }
    } else {
      // When clearing member price, also clear error and trigger re-render
      setMembersPrice('');
      handleValueChange(fields.members_price, '');
      
      // Explicitly clear all validation errors for both fields
      setFormError(fields.members_price, undefined);
      
      // Check if non-member price has min/max validation errors before clearing
      const nonMemberValue = nonMembersPrice ? parseFloat(nonMembersPrice.replace('$', '')) : 0;
      let preserveNonMemberError = false;
      
      if (!isNaN(nonMemberValue)) {
        if ((nonMemberValue > 0 && nonMemberValue < 0.5) || nonMemberValue >= 1000) {
          // Preserve existing min/max validation errors
          preserveNonMemberError = true;
        }
      }
      
      // Only clear non-member price error if there's no min/max error
      if (!preserveNonMemberError) {
        setFormError(fields.non_members_price, undefined);
      }
      
      // Also clear errors in the useForm validation system
      setErrors(prev => ({
        ...prev,
        [fields.members_price]: undefined,
        [fields.non_members_price]: undefined
      }));
      
      // Force re-render of non_members_price by setting the same value
      if (nonMembersPrice) {
        // This will trigger a re-render of the non_members_price Input
        setNonMembersPrice(nonMembersPrice);
      }
    }
  };

  const onChangeNonMembersPrice = event => {
    let value = event.target.value.replace(/[^0-9.]/g, '');
    if (value.length > 0) {
      value = `$${value}`;
      const valueWithoutDollar = value.slice(1);
      handleValueChange(fields.non_members_price, valueWithoutDollar);
      setNonMembersPrice(value);

      // Validate non-member price min/max limits
      const nonMemberValue = parseFloat(valueWithoutDollar);
      if (!isNaN(nonMemberValue)) {
        if (nonMemberValue > 0 && nonMemberValue < 0.5) {
          setFormError(fields.non_members_price, ValidationMessages.MIN_PRICE);
          return;
        } else if (nonMemberValue >= 1000) {
          setFormError(fields.non_members_price, ValidationMessages.MAX_PRICE);
          return;
        }
      }

      // Validate price relationship - use only formErrors for price validation
      if (membersPrice && membersPrice !== '$') {
        const memberValue = parseFloat(membersPrice.replace('$', ''));
        
        if (!isNaN(nonMemberValue) && !isNaN(memberValue) && nonMemberValue < memberValue) {
          setFormError(fields.non_members_price, ValidationMessages.NON_MEMBER_PRICE_LOWER);
        } else {
          setFormError(fields.non_members_price, undefined);
        }
      } else {
        setFormError(fields.non_members_price, undefined);
      }
    } else {
      setNonMembersPrice('');
      handleValueChange(fields.non_members_price, '');
      
      // Clear non-member price error when field is empty
      setFormError(fields.non_members_price, undefined);
      
      // Check if member price exists, add error if needed
      if (membersPrice && membersPrice !== '$') {
        setFormError(fields.non_members_price, ValidationMessages.NON_MEMBER_PRICE_LOWER);
      } else {
        setFormError(fields.non_members_price, undefined);
      }
    }
  };

  const handleCancel = () => {
    setCropModalOpen(false);
    setCroppingUrl();
    setCroppingUrlMimeType();
    setAspectRatio();
    checkType.current = null;
  };

  const handleCrop = cropImg => {
    if (checkType.current === 'desktop') {
      setDesktopCoverPic(cropImg);
      handleValueChange(fields.banner_image_url, cropImg);
    }
  };

  const onChangeDuration = event => {
    let value = event.target.value.replace(/[^0-9]/g, '');
    if (value.length > 2) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }
    if (value.length > 5) {
      value = `${value.slice(0, 5)}:${value.slice(5, 7)}`;
    }
    handleValueChange(fields.duration, value);
    setDuration(value);
  };

  const onChangeTime = event => {
    let value = event.target.value.replace(/[^0-9]/g, '');
    if (value.length > 2) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }
    handleValueChange(fields.start_time, value);
    setTime(value);
  };

  const handleAudioToggle = () => {
    setAudio(!audio);
  };

  const resetPodcastInput = () => {
    if (podcastRef.current) {
      podcastRef.current.value = '';
    }
  };

  const handlePodcastChange = async e => {
    const file = e.target.files[0];
    if (!file) {
      logError('No file selected');
      return;
    }

    if (file && file.type.startsWith('video/')) {
      logInfo('Validating podcast file', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      setFileUploadStage('validating');

      try {
        // Check for quicktime files and create a modified file object with .mp4 extension
        let processedFile = file;
        if (
          file.type === 'video/quicktime' ||
          file.name.toLowerCase().endsWith('.quicktime') ||
          file.name.toLowerCase().endsWith('.qt')
        ) {
          // Create a new file object with .mp4 extension instead of .quicktime/.qt
          const newName = file.name.replace(/\.(quicktime|qt)$/i, '.mp4');
          processedFile = new File([file], newName, { type: 'video/mp4' });
          logInfo('Converted QuickTime file to MP4 format', {
            originalName: file.name,
            newName,
            originalType: file.type,
            newType: processedFile.type
          });
        }

        const validationResult = await validateVideo(processedFile, videoUploadsType.Common);
        logInfo('Validation result', validationResult);

        const { isValid, isResolutionValid, isResolutionTooHigh, isAspectRatioValid, isFileSizeValid, error } = validationResult;

        if (error) {
          logError('Video validation error', { error }, 'error');
          showErrorToast(ValidationErrors.loadingVideo);
          resetPodcastInput();
          setFileUploadStage('');
        } else if (!isFileSizeValid) {
          logError('File size validation failed', { fileSize: file.size }, 'error');
          showErrorToast(ValidationErrors.fileSizeValidation);
          resetPodcastInput();
          setFileUploadStage('');
        } else if (!isResolutionValid) {
          logError('Resolution validation failed', { file }, 'error');
          showErrorToast(ValidationErrors.resolutionValidation);
          resetPodcastInput();
          setFileUploadStage('');
        } else if (isResolutionTooHigh) {
          logError('Resolution too high validation failed', { file }, 'error');
          showErrorToast(ValidationErrors.resolutionTooHighValidation);
          resetPodcastInput();
          setFileUploadStage('');
        } else if (!isAspectRatioValid) {
          logError('Aspect ratio validation failed', { file }, 'error');
          showErrorToast(ValidationErrors.aspectRatioValidationForShorts);
          resetPodcastInput();
          setFileUploadStage('');
        } else if (isValid) {
          logInfo('Validation successful', { fileName: processedFile.name });
          setPodcast(processedFile);
          handleValueChange(fields.podcastVideo, processedFile);
          setFileUploadStage('');
          setCurrentFileName(processedFile.name);
        }
      } catch (error) {
        logError('Validation process error', { error: error.message }, 'error');
        showErrorToast(`Validation error: ${error.message}`);
        resetPodcastInput();
        setFileUploadStage('');
      }
    } else {
      logError(
        'Invalid file type',
        {
          fileName: file?.name,
          fileType: file?.type
        },
        'error'
      );
      showErrorToast(ValidationErrors.validFile);
      resetPodcastInput();
      setFileUploadStage('');
    }
  };

  const handlePodcastUpload = () => {
    if (podcastRef.current) {
      podcastRef.current.click();
    }
  };

  useEffect(() => {
    if (podcastData && categories) {
      setDuration(podcastData?.duration);
      setTime(podcastData.startTime && timeExtract(podcastData.startTime));
      setMembersPrice(podcastData?.membersPrice ? `$${podcastData?.membersPrice}` : '');
      setNonMembersPrice(podcastData?.nonMembersPrice ? `$${podcastData?.nonMembersPrice}` : '');
      setDesktopCoverPic(podcastData?.bannerImageUrl);
      setAudio(podcastData?.audioOnly);
      setPodcast(podcastData?.video?.link);
      const selectedCats = categories?.filter(item =>
        podcastData?.categoryIds.includes(item.value)
      );

      const selectedContent = contentOptions.filter(
        item => item.value === podcastData?.video?.contentViewerCategory
      );
      setSelectedCategories(selectedCats);
      setSelectedContent(selectedContent[0]);
      setValues({
        ...podcastData?.video,
        [fields.lead_cast]: podcastData.video.leadCast,
        [fields.start_date]: podcastData.startDate
          ? new Date(podcastData.startDate).toISOString().split('T')[0]
          : null,
        [fields.start_time]: podcastData.startTime ? timeExtract(podcastData.startTime) : null,
        [fields.members_price]: podcastData.membersPrice ? podcastData.membersPrice : '',
        [fields.non_members_price]: podcastData.nonMembersPrice ? podcastData.nonMembersPrice : '',
        [fields.category_ids]: selectedCats,
        [fields.duration]: podcastData.duration
      });
    }
  }, [podcastData, categories]);

  const getPodcastData = async () => {
    try {
      logInfo('Fetching podcast data', { podcastId, celId });
      const { data } = await PodcastService.getPodcastById(podcastId, celId);
      logInfo('Podcast data received', { podcast: data.podcast });
      setPodcastData(data.podcast);
    } catch (error) {
      const errorResponse = error.response || {};
      logError(
        'Error fetching podcast data',
        {
          status: errorResponse.status,
          data: errorResponse.data,
          message: error.message
        },
        'error'
      );
      throw parseError(response);
    }
  };

  useEffect(() => {
    if (podcastId && celId) {
      getPodcastData();
    }
  }, [podcastId]);

  const getCategories = async () => {
    try {
      const { data } = await categoriesService.getCategories();
      const transformedData = data.categories.map(item => ({
        value: item.id,
        label: item.name
      }));
      setCategories(transformedData);
    } catch ({ response }) {
      throw parseError(response);
    }
  };

  const getProfile = async () => {
    try {
      const { data } = await UserService.getProfile();
      username.current = data.user.username;
      celebrityId.current = data.user.id;
    } catch ({ response }) {
      throw parseError(response);
    }
  };

  // const handleMainCancel = () => {
  //   const route = routeWithProps(routesPaths.celebrityId, {
  //     username: username.current.toLowerCase()
  //   });
  //   goToWithSearch(route, `?tab=4`);
  // };

  const isGlobalLoading = status === LOADING || loading || isLoading || processLoading;
  const hasValidationErrors = some(errors) || some(formErrors);
  const isFormIncomplete =
    !values[fields.title] ||
    !values[fields.description] ||
    !values[fields.contentViewerCategory] ||
    !values[fields.category_ids] ||
    !values[fields.duration] ||
    !values[fields.start_time] ||
    !values[fields.start_date] ||
    !desktopCoverPic ||
    !podcast;

  const isDisabled = isGlobalLoading || hasValidationErrors || isFormIncomplete;

  useEffect(() => {
    getCategories();
    getProfile();
  }, []);

  // Add specific handling for Safari date auto-selection
  useEffect(() => {
    if (isSafari && values[fields.start_date]) {
      // Ensure Safari's auto-selected date is properly included in form validation
      handleValueChange(fields.start_date, values[fields.start_date]);
    }
  }, [isSafari, values[fields.start_date]]);

  return (
    <div className="add-podcast-form">
      <div className="add-podcast-form-fields">
        <Input
          name="title"
          placeholder="Podcast Title"
          label="Title"
          errors={errors[fields.title]}
          {...inputProps(fields.title)}
        />
        <Input
          name="description"
          placeholder="Podcast Description"
          label="Description"
          errors={errors[fields.description]}
          {...inputProps(fields.description)}
        />
        <Input
          name="lead_cast"
          placeholder="Lead cast name"
          label="Lead Cast (optional)"
          errors={errors[fields.lead_cast]}
          {...inputProps(fields.lead_cast)}
        />
        <Input
          name="credits"
          placeholder="Credits for Podcast"
          label="Credit For Podcast (optional)"
          errors={errors[fields.credits]}
          {...inputProps(fields.credits)}
        />
        <CustomCategory value={selectedCategories} handleCategoryChange={handleCategoryChange} />
        <div className={cn('custom-short-content')}>
          <span className="custom-short-content-label">Content Viewers</span>
          <div className="single-select-short">
            <Select
              value={selectedContent}
              styles={customDropDownStyles}
              options={contentOptions}
              isSearchable={false}
              onChange={handleContentChange}
            />
          </div>
        </div>
        <Input
          name="duration"
          placeholder="HH:MM:SS"
          label="Duration"
          errors={errors[fields.duration]}
          {...inputProps(fields.duration)}
          maxLength={8}
          value={duration}
          onChange={onChangeDuration}
        />
        <div className="input-container">
          <div className="input-card">
            <div className="d-flex">
              <Input
                name="date"
                type="date"
                placeholder="DD/MM/YYYY"
                label="Date"
                value={values[fields.start_date]}
                onKeyDown={handleDateKeyDown}
                onChange={e => {
                  // Handle Safari's date auto-selection
                  const newValue = e.target.value;
                  handleValueChange(fields.start_date, newValue);
                  
                  // If in Safari and this is an initial auto-selection, ensure form validation is triggered
                  if (isSafari && newValue && !time) {
                    // Set a default time if it's not set yet
                    handleValueChange(fields.start_time, '00:00');
                    setTime('00:00');
                  }
                }}
                min={getTodayDate()}
              />
              {/* <Input
                name="time"
                type="time"
                placeholder="HH:MM:SS"
                label="Time"
                value={values[fields.start_time]}
                onKeyDown={handleDateKeyDown}
                onChange={e => handleValueChange(fields.start_time, e.target.value)}
              /> */}
              <Input
                name="time"
                placeholder="HH:MM"
                label="Time"
                errors={errors[fields.start_time]}
                {...inputProps(fields.start_time)}
                maxLength={5}
                value={time}
                onChange={onChangeTime}
              />
            </div>
          </div>
        </div>
        <div className="input-container">
          <div className="d-flex">
            <Input
              name="memberPrice"
              placeholder="Price"
              label="Member price"
              errors={errors[fields.members_price]}
              {...inputProps(fields.members_price)}
              onChange={onChangeMembersPrice}
              value={membersPrice}
            />
            <Input
              name="nonMemberPrice"
              placeholder="price"
              label="non-member price"
              errors={formErrors[fields.non_members_price]}
              {...inputProps(fields.non_members_price)}
              onChange={onChangeNonMembersPrice}
              value={nonMembersPrice}
            />
          </div>
          <span style={{ margin: 0 }} className="label-margin">
            *Leave this field empty for free content.
          </span>
        </div>
        <input
          type="file"
          ref={desktopCoverRef}
          style={{ display: 'none' }}
          accept="image/*,.heic,.heif"
          name="profile_image"
          onChange={handleDesktopCoverChange}
        />
        <div className="switch-container">
          <div className="switch-part">
            <span>
              <Switch
                checked={audio}
                onChange={handleAudioToggle}
                onColor="#2400FC"
                offColor="#ccc"
                checkedIcon={false}
              />
            </span>
            <span className="switch-label">Audio Only</span>
          </div>
          {audio && <span className="support-margin">Supported files - MP4</span>}
        </div>
        <div>
          <span className={desktopCoverPic?.name ? 'white-custom-label' : 'custom-label'}>
            Podcast Cover
          </span>
          {podcastId && desktopCoverPic && !isDesktopBannerFalse && (
            <div className="display-loaded-pic">
              <img className="url-pic" src={podcastData?.bannerImageUrl} alt="desktop-banner" />
              <Button
                labelId="Change"
                type="secondary"
                onClick={() => {
                  setDesktopCoverPic(null);
                  setIsDesktopBannerFalse(true);
                  handleDesktopCoverUpload();
                }}
                className="change-button"
              />
            </div>
          )}
          {desktopCoverPic && (!podcastId || isDesktopBannerFalse) && (
            <div className="white-label">
              <span className="white-name">{desktopCoverPic?.name}</span>
              <span
                className="white-name"
                style={{ cursor: 'pointer' }}
                onClick={() => setDesktopCoverPic(null)}
              >
                x
              </span>
            </div>
          )}
          {!desktopCoverPic && (
            <div className="button-margin">
              <Button
                title="UPLOAD PODCAST COVER IMAGE"
                type="secondary"
                onClick={handleDesktopCoverUpload}
                className="profile-button"
              />
              <span className="label-margin">Recommended dimension: 800px X 450px (16:9)</span>
            </div>
          )}
        </div>
        <input
          type="file"
          ref={podcastRef}
          style={{ display: 'none' }}
          accept=".mp4,.mov"
          name="podcast"
          onChange={handlePodcastChange}
        />
        <div>
          <span className="custom-label">UPLOAD PODCAST</span>
          {podcast ? (
            <div>
              <video className="video-preview" preload="metadata" playsInline autoPlay>
                <source
                  src={typeof podcast === 'string' ? podcast : URL.createObjectURL(podcast)}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <div className="button-margin">
              <Button
                labelId="UPLOAD PODCAST"
                type="secondary"
                onClick={handlePodcastUpload}
                className="profile-button"
              />
              <span className="label-margin">
                Recommended dimension: (1:1, 3:2, 4:3, 16:9, 2:3, 3:4, 9:16)
              </span>
            </div>
          )}
        </div>
        {!podcastId && (
          <div className={cn('notify-box')}>
            <input
              type="checkbox"
              checked={subscribedFans}
              onChange={() => {
                setSubScribedFans(!subscribedFans);
                handleValueChange(fields.notify_subscribers, !subscribedFans);
              }}
            />
            Notify all paid fans
          </div>
        )}
      </div>

      {/* Error logs are auto-displayed */}
      <LogViewer
        logs={logs}
        showLogs={showLogs}
        setShowLogs={setShowLogs}
        title="Podcast Upload Errors"
      />

      <div className="creator-form-error p1">{error}</div>
      {(fileUploadProgress > 0 || fileUploadStage) && fileUploadStage !== 'completed' && (
        <div
          style={{
            marginBottom: '15px',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '15px',
            borderRadius: '8px',
            width: '100%'
          }}
        >
          <div style={{ textAlign: 'center', marginTop: '5px', color: 'white', width: '100%' }}>
            {fileUploadStage === 'validating' && (
              <div className="upload-status">
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Validating Files</div>
                <div style={{ fontSize: '0.9em' }}>Checking file validations before upload...</div>
              </div>
            )}
            {fileUploadStage === 'validated' && (
              <div className="upload-status">
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Validation Passed</div>
                <div style={{ fontSize: '0.9em' }}>Preparing files for upload...</div>
              </div>
            )}
            {fileUploadStage === 'uploading' && (
              <div className="upload-status">
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Uploading Podcast</div>
                <div style={{ 
                  fontSize: '0.9em', 
                  marginTop: '5px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%'
                }}>
                  {currentFileName || 'Current file'}
                </div>
              </div>
            )}
            {fileUploadStage === 'processing' && (
              <div className="upload-status">
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Processing</div>
                <div style={{ fontSize: '0.9em' }}>Processing your podcast...</div>
              </div>
            )}
          </div>
          <div style={{ width: '100%', padding: '10px 0' }}>
            <Line
              percent={fileUploadProgress || 0}
              strokeWidth={2}
              strokeColor="#2400FC"
              trailColor="#444"
            />
          </div>
          <div
            style={{
              textAlign: 'center',
              marginTop: '5px',
              marginBottom: '5px',
              color: 'white',
              fontSize: '0.9em'
            }}
          >
            {fileUploadProgress || 0}% Complete
          </div>
        </div>
      )}
      {fileUploadStage === 'completed' && (
        <div
          style={{
            marginBottom: '15px',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '15px',
            borderRadius: '8px',
            width: '100%'
          }}
        >
          <div className="upload-status">
            <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#4caf50' }}>Upload Completed</div>
            <div style={{ fontSize: '0.9em' }}>Your podcast has been uploaded successfully!</div>
          </div>
        </div>
      )}
      {(status === LOADING || isLoading || processLoading) && (
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'center' }}>
          <Loading type="ball-clip-rotate" />
        </div>
      )}
      <div className="btn-container">
        {/* <Button
          title="Cancel"
          className="profile-button"
          type="secondary"
          onClick={handleMainCancel}
        /> */}
        <Button
          title={podcastId ? 'Save Changes' : 'Save Podcast'}
          className="submit-button"
          onClick={handleSubmit}
          type="submit"
          disabled={isDisabled}
        />
      </div>
      {cropModalOpen && (
        <ImageCropperModal
          visible={cropModalOpen}
          imageUrl={croppingUrl}
          onCancel={handleCancel}
          onCrop={handleCrop}
          aspectRatio={aspectRatio}
          mimeType={croppingUrlMimeType === 'image/png' ? croppingUrlMimeType : 'image/jpeg'}
        />
      )}
    </div>
  );
};

export default memo(AddPodcastForm);
